import { useEffect, useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  Alert,
  Box,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import Refresh from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { confirmAsync } from "../../../utils/appNotify";
import {
  deleteDepartment,
  getAllDepartment,
} from "../../../services/departmentService";
import DepartmentModal from "./DepartmentModal";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import dayjs from "dayjs";
import {
  filterDepartmentsForPackage,
  resolveDepartmentCareScope,
} from "../../../utils/subscriptionScopeUtils";

const columns = [
  { id: "id", label: "Id" },
  { id: "name", label: "Department Name" },
  { id: "code", label: "Code" },
  { id: "departmentType", label: "Type" },
  { id: "description", label: "Description" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const TYPE_LABELS = {
  OPD: "OPD",
  BOTH: "OPD + IPD",
  IPD: "IPD",
};

const resolveDepartmentType = (row) => {
  const explicit =
    row?.departmentType ||
    row?.department_type ||
    row?.type ||
    row?.category;
  if (explicit && String(explicit).trim() && String(explicit) !== "—") {
    const upper = String(explicit).toUpperCase();
    if (upper === "BOTH" || upper === "OPD+IPD") return "BOTH";
    if (upper === "IPD") return "IPD";
    if (upper === "OPD") return "OPD";
    return upper;
  }
  return resolveDepartmentCareScope(row);
};

const resolveDepartmentCode = (row) => row?.code || row?.departmentCode || "—";

const resolveDepartmentDescription = (row) =>
  row?.description || row?.departmentDescription || "—";

export default function DepartmentsTable() {
  const { isPremiumPackage } = useSubscriptionCapabilities();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [departments, setDepartments] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchDepartmentList();
  }, []);

  const fetchDepartmentList = async () => {
    setFetching(true);
    try {
      const data = await getAllDepartment();
      const sorted = [...(Array.isArray(data) ? data : [])].sort(
        (a, b) => Number(b.id ?? 0) - Number(a.id ?? 0)
      );
      setDepartments(sorted);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    } finally {
      setFetching(false);
    }
  };

  const handleAddNew = () => {
    setDepartmentInfo(null);
    setOpenModal(true);
  };

  const handleEdit = (department) => {
    setDepartmentInfo(department);
    setOpenModal(true);
  };

  const handleDelete = async (department) => {
    const confirmed = await confirmAsync(
      `Are you sure you want to delete department "${department.name}"?`
    );
    if (!confirmed) return;

    try {
      await deleteDepartment(department.id);
      await fetchDepartmentList();
    } catch (error) {
      console.error("Failed to delete department:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete department. It may be assigned to doctors or staff.";
      window.alert(message);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    setOpenModal(false);
    setDepartmentInfo(null);
    if (shouldRefresh) {
      fetchDepartmentList();
    }
  };

  const packageScopedDepartments = useMemo(
    () =>
      filterDepartmentsForPackage(departments, {
        isPremium: isPremiumPackage,
      }),
    [departments, isPremiumPackage]
  );

  const normalizedRows = packageScopedDepartments.map((row) => {
    const careScope = resolveDepartmentType(row);
    return {
      ...row,
      code: resolveDepartmentCode(row),
      departmentType: careScope,
      departmentTypeLabel: TYPE_LABELS[careScope] || careScope,
      description: resolveDepartmentDescription(row),
    };
  });

  const filteredRows = normalizedRows.filter((row) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [
        row.id,
        row.name,
        row.code,
        row.departmentType,
        row.departmentTypeLabel,
        row.description,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      if (!vals.some((v) => v.includes(q))) return false;
    }
    if (filterName.trim()) {
      if (!String(row.name || "").toLowerCase().includes(filterName.toLowerCase())) {
        return false;
      }
    }
    if (filterType) {
      if (String(row.departmentType || "") !== filterType) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterType("");
  };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Id", "Department Name", "Code", "Type", "Description"];
    const tableRows = rows.map((r) => [
      r.id,
      r.name,
      r.code,
      r.departmentTypeLabel || r.departmentType,
      r.description,
    ]);

    await downloadStyledTableExcel({
      filename: `departments_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Departments",
      title: "Departments",
      tableHeaders: headers,
      tableRows,
    });
  };

  const typeOptions = [
    ...new Set(normalizedRows.map((d) => d.departmentType).filter((t) => t && t !== "—")),
  ];

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={handleAddNew}
        >
          Add Department
        </Button>
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={fetchDepartmentList}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
            <Stack
              spacing={1}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mt: 1 }}
            >
              <TextField
                size="small"
                label="Name"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
              />
              <TextField
                size="small"
                label="Type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                select
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All</MenuItem>
                {typeOptions.map((t) => (
                  <MenuItem key={t} value={t}>
                    {TYPE_LABELS[t] || t}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="Clear filters">
                <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

        <TableContainer sx={{ maxHeight: 550 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell key={column.id}>{column.label}</StyledTableCell>
                ))}
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Alert severity="info">No Departments Available</Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      {columns.map((column) => (
                        <TableCell key={column.id}>
                          {column.id === "departmentType"
                            ? row.departmentTypeLabel || row.departmentType
                            : row[column.id]}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Tooltip title="Edit Department">
                          <IconButton
                            size="small"
                            sx={TABLE_ICON_BUTTON_SX}
                            onClick={() => handleEdit(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Department">
                          <IconButton
                            size="small"
                            color="error"
                            sx={TABLE_ICON_BUTTON_SX}
                            onClick={() => handleDelete(row)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      <DepartmentModal
        open={openModal}
        onClose={handleModalClose}
        departmentInfo={departmentInfo}
      />
    </Grid>
  );
}
